import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Button } from '@material-ui/core';
import { Check } from '@material-ui/icons';
import { size } from 'lodash/fp';
import { Grid, Label } from '../../common/components';
import messages from '../messages';
import TargetSelectDialog from './TargetSelectDialog';

const S = {
  Button: styled(({ error, ...otherProps }) => <Button {...otherProps} />)`
    min-width: 90px;
    height: 40px;
    margin-right: 1.5rem;
    border: 1px solid ${({ theme }) => theme.palette.primary.main};
    ${({ error, theme }) => error && css`
      border-color: ${theme.palette.error.main};
      color: ${theme.palette.error.main};
      :hover {
        border-color: ${theme.palette.error.main};
        background-color: rgba(225, 88, 99, 0.08);
      }
      :focus {
        border: 2px solid ${theme.palette.error.main};
        background-color: rgba(225, 88, 99, 0.08);
      }
    `}
  `,
  Check: styled(Check)`
    margin-right: 0.25rem;
    color: ${({ theme }) => theme.palette.secondary.main};
    font-size: 1.5rem;
  `,
  TargetCounter: styled.div`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.palette.secondary.main};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
  `,
};

const TargetField = ({
  className, groups, targets, field: { onChange, ...field }, form,
}) => {
  const [open, setOpen] = useState(false);

  const error = useMemo(() => !!(
    form.errors[field.name] && form.touched[field.name]
  ), [field.name, form.errors, form.touched]);

  const handleClick = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    form.setFieldTouched(field.name, true);
    setOpen(false);
  }, [field.name, form]);

  return (
    <>
      <Grid
        container
        spacing={2}
        alignItems="center"
        className={className}
      >
        <Grid item xs={1}>
          <Label
            htmlFor={field.name}
            error={error}
            required
          >
            <FormattedMessage {...messages.targetFieldLabel} />
          </Label>
        </Grid>
        <Grid container item xs={6}>
          <S.Button
            type="button"
            error={error}
            color="primary"
            variant="outlined"
            onClick={handleClick}
          >
            <FormattedMessage
              {...messages.targetFieldButton}
            />
          </S.Button>
          {field.value && (
            <S.TargetCounter>
              <S.Check />
              {`총 ${size(field.value)}명`}
            </S.TargetCounter>
          )}
        </Grid>
      </Grid>
      <TargetSelectDialog
        form={form}
        groups={groups}
        targets={targets}
        open={open}
        onClose={handleClose}
      />
    </>
  );
};

TargetField.propTypes = {
  className: PropTypes.string,
  groups: PropTypes.shape({
    data: PropTypes.shape({
      clazzGroups: PropTypes.arrayOf(PropTypes.shape({
        class_name: PropTypes.string,
        contacts_count: PropTypes.number,
        created_at: PropTypes.string,
        grade: PropTypes.number,
        id: PropTypes.number,
        updated_at: PropTypes.string,
      })),
      customGroups: PropTypes.arrayOf(PropTypes.shape({
        contacts_count: PropTypes.number,
        created_at: PropTypes.string,
        id: PropTypes.number,
        is_unspecified_group: PropTypes.bool,
        name: PropTypes.string,
        updated_at: PropTypes.string,
      })),
    }),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
  }).isRequired,
  targets: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      class_group: PropTypes.shape({
        class_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        contacts_count: PropTypes.number,
        created_at: PropTypes.string,
        grade: PropTypes.number,
        id: PropTypes.number,
        updated_at: PropTypes.string,
      }),
      class_name: PropTypes.string,
      created_at: PropTypes.string,
      custom_groups: PropTypes.arrayOf(PropTypes.shape({
        contacts_count: PropTypes.number,
        created_at: PropTypes.string,
        id: PropTypes.number,
        is_unspecified_group: PropTypes.bool,
        name: PropTypes.string,
        updated_at: PropTypes.string,
      })),
      email: PropTypes.string,
      grade: PropTypes.number,
      id: PropTypes.number,
      mobile: PropTypes.string,
      name: PropTypes.string,
      number: PropTypes.number,
      primary_guardian_contact: PropTypes.shape({
        contact_id: PropTypes.number,
        created_at: PropTypes.string,
        email: PropTypes.string,
        id: PropTypes.number,
        level: PropTypes.string,
        mobile: PropTypes.string,
        name: PropTypes.string,
        relationship: PropTypes.string,
        updated_at: PropTypes.string,
        user_id: PropTypes.number,
      }),
    })),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
  }).isRequired,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
      })),
    ]),
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({
      title: PropTypes.string,
    }).isRequired,
    touched: PropTypes.shape({
      title: PropTypes.bool,
    }).isRequired,
    setFieldTouched: PropTypes.func,
  }).isRequired,
};

TargetField.defaultProps = {
  className: undefined,
};

export default TargetField;
